import _ from 'lodash';
import classnames from 'classnames';
import React from 'react';
import ReactTooltip from 'react-tooltip';
import { Modal } from 'design-system/Modal';
import Textarea from 'design-system/Textarea';
import { Button } from 'design-system/Form';
import { Button as NewButton } from '@scale/scaleui';

import {
  TaskFeedbackInput,
  ITaskFeedbackEntry,
  reviewerRatingFeedbackEntry,
  asFeedbackEntryChoice,
} from '@scale/shared/taskFeedback';

function isReviewerRating(entry: ITaskFeedbackEntry): boolean {
  const sameChoices = entry.choices?.every(choice => {
    if (typeof choice === 'string') {
      return false;
    }
    return reviewerRatingFeedbackEntry.choices?.some(ratingChoice => {
      if (typeof ratingChoice === 'string') return false;
      return ratingChoice.value === choice.value && ratingChoice.label === choice.label;
    });
  });

  const sameTitle = entry.title === reviewerRatingFeedbackEntry.title;
  return sameTitle && Boolean(sameChoices);
}

interface ITemplateFeedbackProps {
  handleSubmitFeedback: (
    textReasons: Record<string, string>,
    selectReasons: Record<string, string[]>,
  ) => void;
  enableOtherReason?: boolean;
  buttonText: string;
  style?: React.CSSProperties;
  confirmButtonText?: string;
  feedbackBodyText?: string;
  feedbackHeaderText?: string;
  feedbackEntries: ITaskFeedbackEntry[];
  disabled?: boolean;
  modernButtonStyle?: boolean;
  variant?: 'danger' | 'success';
}

interface ITemplateFeedbackState {
  isModalOpen: boolean;
  textReasons: Record<string, string>;
  selectReasons: Record<string, string[]>;
}

const defaultState: ITemplateFeedbackState = {
  isModalOpen: false,
  textReasons: {},
  selectReasons: {},
};

class TemplateFeedback extends React.Component<ITemplateFeedbackProps, ITemplateFeedbackState> {
  constructor(props: ITemplateFeedbackProps) {
    super(props);
    this.state = defaultState;
  }

  openModal = () => {
    this.setState({ isModalOpen: true });
  };

  closeModal = () => {
    this.setState(defaultState);
  };

  // if a selection choice is present in this.state.selectReasons, it is removed. If it is not present, it is added
  updateSelectResponses(
    inputType: TaskFeedbackInput,
    title: string,
    choice: string,
  ): ITemplateFeedbackState['selectReasons'] {
    const clonedSelectReasons = _.cloneDeep(this.state.selectReasons);
    if (inputType === TaskFeedbackInput.Select) {
      const onlySingleReason: string[] = [];
      onlySingleReason.push(choice);
      clonedSelectReasons[title] = onlySingleReason;
    } else if (inputType === TaskFeedbackInput.MultiSelect) {
      if (title in clonedSelectReasons) {
        if (clonedSelectReasons[title].includes(choice)) {
          const index = clonedSelectReasons[title].indexOf(choice);
          clonedSelectReasons[title].splice(index, 1);
        } else {
          clonedSelectReasons[title].push(choice);
        }
      } else {
        clonedSelectReasons[title] = [choice];
      }
    }
    return clonedSelectReasons;
  }

  renderSelectFeedbackEntry(entry: ITaskFeedbackEntry) {
    const inputType: TaskFeedbackInput = entry.inputType;
    const title: string = entry.title;
    const choices: ITaskFeedbackEntry['choices'] = isReviewerRating(entry)
      ? reviewerRatingFeedbackEntry.choices
      : entry.choices;
    const key = isReviewerRating(entry) ? `${title}-${entry.description}` : title;

    return (
      <div className="reason">
        <div>
          {choices?.map(_choice => {
            const choice = asFeedbackEntryChoice(_choice);
            const onClick = () => {
              this.setState({
                selectReasons: this.updateSelectResponses(inputType, key, choice.value),
              });
            };

            return (
              <div
                key={choice.value}
                className={classnames('choice_option', {
                  selected: this.state.selectReasons[key]?.includes(choice.value),
                })}
                onClick={onClick}
              >
                {choice.label}
              </div>
            );
          })}
        </div>
        <style jsx>{`
          .reason {
            color: rgba(0, 0, 0, 0.6);
            font-size: 14px;
            font-weight: bold;
          }

          .choice_option {
            display: inline-block;
            margin: 12px 12px 12px 0;
            box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
            border-radius: var(--border-radius);
            padding: 8px 16px;
            transition: all 0.2s;
            cursor: pointer;
          }

          .choice_option:hover {
            box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
          }

          .choice_option.selected {
            background: var(--link-color);
            color: white;
            cursor: default;
          }
        `}</style>
      </div>
    );
  }

  updateTextResponses(title: string, input: string): ITemplateFeedbackState['textReasons'] {
    const clonedTextReasons = _.cloneDeep(this.state.textReasons);
    clonedTextReasons[title] = input;
    return clonedTextReasons;
  }

  renderTextFeedbackEntry(entry: ITaskFeedbackEntry) {
    const title: string = entry.title;
    return (
      <Textarea
        onChange={e =>
          this.setState({ textReasons: this.updateTextResponses(title, e.target.value) })
        }
        placeholder="Enter text here"
      ></Textarea>
    );
  }

  renderEntry(entry: ITaskFeedbackEntry) {
    if (entry.inputType === TaskFeedbackInput.Text) {
      return this.renderTextFeedbackEntry(entry);
    } else {
      return this.renderSelectFeedbackEntry(entry);
    }
  }

  disableHelper() {
    for (const entry of this.props.feedbackEntries) {
      if (entry.required !== undefined && !entry.required) {
        continue;
      }
      const key = isReviewerRating(entry)
        ? `${reviewerRatingFeedbackEntry.title}-${entry.description}`
        : entry.title;
      const searchState =
        entry.inputType === TaskFeedbackInput.Text
          ? this.state.textReasons
          : this.state.selectReasons;
      if (!(key in searchState) || searchState[key].length === 0) {
        return true;
      }
    }
    return false;
  }

  renderModal() {
    const disabled = this.disableHelper();

    return (
      <Modal
        isOpen={this.state.isModalOpen}
        onRequestClose={this.closeModal}
        contentLabel="Modal"
        maxWidth="640px"
      >
        <div className="feedback">
          <div className="intro">
            <h2>{this.props.feedbackHeaderText || 'Please Provide Feedback'}</h2>
            <p>{this.props.feedbackBodyText || 'Please provide feedback.'}</p>
          </div>

          {this.props.feedbackEntries.map((entry, index) => (
            <div className="section" key={`${entry.title}-${index}`}>
              <div className="label">
                {entry.inputType === TaskFeedbackInput.MultiSelect
                  ? entry.title + ' (select all that apply)'
                  : entry.title}
                {(entry.required === undefined || entry.required) && ( // previouse entry doesn't contain required property, but they are default true
                  <>
                    <span data-tip data-for="asterisk" className="asterisk">
                      *
                    </span>
                    <ReactTooltip id="asterisk">
                      <span>require to answer</span>
                    </ReactTooltip>
                  </>
                )}
              </div>
              {entry.description && <div className="description">{entry.description}</div>}
              {this.renderEntry(entry)}
            </div>
          ))}

          <div>
            <div className="submit">
              <div
                data-tip={disabled ? 'Please complete the feedback form.' : null}
                data-for="feedback-disabled"
              >
                <Button
                  size="lg"
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    this.props.handleSubmitFeedback(
                      this.state.textReasons,
                      this.state.selectReasons,
                    );
                    this.closeModal();
                  }}
                >
                  {this.props.confirmButtonText || 'Submit'}
                </Button>
                <ReactTooltip id="feedback-disabled" />
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          .feedback {
            padding: 32px;
          }

          .intro {
            font-size: 16px;
            font-weight: bold;
          }

          .intro h2 {
            margin: 0;
            font-size: 20px;
          }

          .intro p {
            margin: 16px 0 32px;
            line-height: 1.7;
          }

          .section {
            margin: 36px 0;
          }

          .section .label {
            margin-bottom: 8px;
            text-transform: uppercase;
            font-weight: bold;
            font-size: 13px;
            letter-spacing: 0.5px;
          }

          .section .description {
            margin-bottom: 8px;
            font-size: 13px;
            color: rgba(0, 0, 0, 0.45);
            letter-spacing: 0.5px;
            inline-size: 100%;
            overflow-wrap: break-word;
          }

          .submit {
            display: flex;
            justify-content: center;
          }
          .asterisk {
            color: red;
          }
        `}</style>
      </Modal>
    );
  }

  render() {
    const btn = this.props.modernButtonStyle ? (
      <NewButton
        style={this.props.style}
        key="templateFeedbackButton"
        onClick={this.openModal}
        disabled={this.props.disabled}
        variant={this.props.variant}
      >
        {this.props.buttonText}
      </NewButton>
    ) : (
      <Button
        key="templateFeedbackButton"
        type="button"
        style={this.props.style}
        onClick={this.openModal}
        disabled={this.props.disabled}
      >
        {this.props.buttonText}
      </Button>
    );
    return (
      <div>
        {btn}
        {this.state.isModalOpen && this.renderModal()}
      </div>
    );
  }
}

export default TemplateFeedback;
